import type { ClassPublication, LiveBoardSession } from "./gameMode.js";
import type { SimulatorAppManifest } from "./simulatorApps.js";
import type { TargetDefinition, TargetResult, TargetVisibilitySummary } from "./targetVisibility.js";
import { buildStudentTargetSummary, revealHiddenAfterCompletion } from "./targetVisibility.js";
import type { TicketTemplate } from "./tickets.js";

export type WorkspaceKind = "student" | "teacher" | "admin";

export type TicketStatus = "open" | "in_progress" | "review_required" | "closed";

export type TicketPriority = "laag" | "gemiddeld" | "hoog";

export interface TicketWorkspaceItem {
  id: string;
  number: string;
  title: string;
  description: string;
  status: TicketStatus;
  priority: TicketPriority;
  type: "Game" | "Servicedesk";
  topic: string;
  complexity: string;
  environment: string;
  dueLabel: string;
  reporter: string;
  teacher: string;
  targets: TargetDefinition[];
  results: TargetResult[];
}

export interface StudentTicketWorkspaceItem extends Omit<TicketWorkspaceItem, "targets" | "results"> {
  targetSummary: TargetVisibilitySummary;
}

export interface StudentSimulatorAppView {
  appId: string;
  name: string;
  category: SimulatorAppManifest["category"];
}

export interface StudentClassView {
  classGroupId: string;
  className: string;
  classCode: string;
}

export interface StudentBadgeLevelView {
  key: string;
  title: string;
  minCompletedTickets: number;
  unlocked: boolean;
  remaining: number;
}

export interface StudentBadgeSummaryView {
  completedTickets: number;
  active: StudentBadgeLevelView | null;
  next: StudentBadgeLevelView | null;
  levels: StudentBadgeLevelView[];
}

export interface StudentProfileView {
  name: string;
  email: string;
  role: string;
  institutionName: string;
  createdAt: string;
  lastLoginAt: string;
}

export interface LiveBoardRow {
  attemptId: string;
  ticketNumber: string;
  ticketTitle: string;
  studentName: string;
  status: "Nog niet gestart" | "Bezig" | "Ingeleverd" | "Feedback klaar";
  completedTargets: number;
  totalTargets: number;
  hiddenCompleted: number;
  scoreLabel: string;
  lastActivity: string;
}

export interface StudentWorkspaceSnapshot {
  tickets: StudentTicketWorkspaceItem[];
  simulatorApps: StudentSimulatorAppView[];
  classes: StudentClassView[];
  badge: StudentBadgeSummaryView;
  profile: StudentProfileView;
}

export interface TeacherWorkspaceSnapshot {
  template: TicketTemplate;
  activePublication: ClassPublication;
  liveBoardSession: LiveBoardSession;
  liveBoardRows: LiveBoardRow[];
  classes: TeacherClassView[];
  tickets: TicketWorkspaceItem[];
}

export interface TeacherClassAttemptView {
  attemptId: string;
  studentName: string;
  ticketNumber: string;
  ticketTitle: string;
  status: LiveBoardRow["status"];
}

export interface TeacherClassView {
  classGroupId: string;
  className: string;
  classCode: string;
  studentCount: number;
  publicationCount: number;
  activePublicationCount: number;
  attempts: TeacherClassAttemptView[];
}

export interface AdminTemplateListItem {
  id: string;
  title: string;
  topic: string;
  complexity: string;
  status: string;
  updatedLabel: string;
}

export interface AdminWorkspaceSnapshot {
  template: TicketTemplate;
  simulatorApps: SimulatorAppManifest[];
  migrationSteps: string[];
  manifestCount: number;
  activeManifestCount: number;
  recentTemplates: AdminTemplateListItem[];
}

export interface V2WorkspaceSnapshot {
  student: StudentWorkspaceSnapshot;
  teacher: TeacherWorkspaceSnapshot;
  admin: AdminWorkspaceSnapshot;
}

export function toStudentTicket(ticket: TicketWorkspaceItem): StudentTicketWorkspaceItem {
  const revealAfterCompletion = revealHiddenAfterCompletion(ticket.status, ticket.results.length > 0);

  return {
    id: ticket.id,
    number: ticket.number,
    title: ticket.title,
    description: ticket.description,
    status: ticket.status,
    priority: ticket.priority,
    type: ticket.type,
    topic: ticket.topic,
    complexity: ticket.complexity,
    environment: ticket.environment,
    dueLabel: ticket.dueLabel,
    reporter: ticket.reporter,
    teacher: ticket.teacher,
    targetSummary: buildStudentTargetSummary(ticket.targets, ticket.results, {
      revealHiddenAfterCompletion: revealAfterCompletion
    })
  };
}

export function buildStudentWorkspaceSnapshot(
  tickets: TicketWorkspaceItem[],
  simulatorApps: SimulatorAppManifest[],
  classes: StudentClassView[] = [],
  badge: StudentBadgeSummaryView = buildStudentBadgeSummary(tickets),
  profile: StudentProfileView = {
    name: "Student",
    email: "-",
    role: "Student",
    institutionName: "-",
    createdAt: "-",
    lastLoginAt: "-"
  }
): StudentWorkspaceSnapshot {
  return {
    tickets: tickets.map((ticket) => toStudentTicket(ticket)),
    simulatorApps: simulatorApps.map((app) => ({
      appId: app.appId,
      name: app.name,
      category: app.category
    })),
    classes,
    badge,
    profile
  };
}

const studentBadgeLevels = [
  { key: "tier2", title: "Tweedelijns Medewerker", minCompletedTickets: 10 },
  { key: "tier3", title: "Derdelijns Medewerker", minCompletedTickets: 50 },
  { key: "senior", title: "Senior Servicedesk Specialist", minCompletedTickets: 100 }
];

export function buildStudentBadgeSummary(tickets: TicketWorkspaceItem[]): StudentBadgeSummaryView {
  const completedTickets = tickets.filter((ticket) => ticket.status === "closed").length;
  const levels = studentBadgeLevels.map((level) => ({
    ...level,
    unlocked: completedTickets >= level.minCompletedTickets,
    remaining: Math.max(0, level.minCompletedTickets - completedTickets)
  }));
  const active = [...levels].reverse().find((level) => level.unlocked) ?? null;
  const next = levels.find((level) => !level.unlocked) ?? null;

  return {
    completedTickets,
    active,
    next,
    levels
  };
}
